"use client";

import { useEffect } from "react";
import { toast } from "sonner";
import {
  isOnline,
  onConnectivityChange,
  probeConnectivity,
  startConnectivityWatch,
} from "@/features/forms/offline/connectivity";
import {
  autoOfflineTargetFromLocation,
  didRecentlyAutoSwitch,
  hasPrecachedOfflineShell,
  isOfflineShellDocument,
  recordAutoSwitch,
} from "@/features/forms/offline/auto-switch";

/**
 * Notices that the network is gone, and switches the field app into offline mode
 * instead of leaving the user tapping a screen that cannot respond.
 *
 * Mounted once, app-wide (app/layout.tsx), for two reasons: the connectivity
 * watch is the app's single source of truth about the network — every offline
 * banner, sync trigger and gate reads the verdict it maintains — and the drop can
 * happen on any page, not only the ones that know how to handle it.
 *
 * What it does NOT do is redirect anyone it can't help: `autoOfflineTargetFrom
 * Location` returns null for the desktop app, public form fills and the offline
 * documents themselves. On those pages this component is just the watch, and the
 * existing offline banners do the talking.
 */
export function OfflineAutoSwitch() {
  useEffect(() => {
    // One in-document guard on top of the cross-document cooldown: connectivity
    // can flap, and two verdicts arriving a moment apart must not both navigate.
    let switching = false;
    // Say "offline mode isn't available here" once, not on every poll.
    let warnedNoShell = false;

    const maybeSwitch = async () => {
      if (switching || isOnline()) return;
      // This document is the offline app already — there is nowhere to go.
      if (isOfflineShellDocument()) return;

      const target = autoOfflineTargetFromLocation(window.location);
      if (!target) return;
      if (didRecentlyAutoSwitch(window.location.pathname)) return;

      /**
       * Only if this device HAS an offline app to switch to. Without the
       * precached shell the navigation lands on the browser's offline error page
       * — strictly worse than the screen they're already on. (The offline-check
       * page is where a device in that state gets diagnosed and repaired.)
       */
      if (!(await hasPrecachedOfflineShell())) {
        if (warnedNoShell) return;
        warnedNoShell = true;
        toast("No internet", {
          description:
            "Offline mode isn't set up on this device yet — some things won't work until you reconnect.",
          duration: 8000,
        });
        return;
      }
      if (switching) return;

      switching = true;
      recordAutoSwitch(window.location.pathname);

      /**
       * The switch is not instant: the service worker gives the network 3s to
       * produce a document before it serves the shell (app/sw.ts). Say what's
       * happening, or those 3s look like the same freeze we're fixing.
       */
      toast("No internet — switching to offline mode", {
        description: "Your saved jobs and forms stay available.",
        duration: 6000,
      });

      /**
       * A full document load, deliberately: a client-side navigation would fetch
       * from a network that isn't there (the freeze), while a document request is
       * the one thing the service worker can answer from the precache.
       *
       * `replace`, not `assign`: the target is usually the url we're already on,
       * and stacking a duplicate history entry would make Back a no-op. In-flight
       * form answers survive — drafts flush to IndexedDB on pagehide
       * (useOfflineDraft), which a document navigation fires.
       */
      window.location.replace(target);
    };

    const stopWatch = startConnectivityWatch();
    const unsubscribe = onConnectivityChange(() => void maybeSwitch());

    // Cover the case where the app is ALREADY offline as this mounts — a cold
    // launch with no signal, or a resume that reloaded the page. The watch probes
    // on start too; this just makes the follow-up explicit.
    void probeConnectivity().then(() => maybeSwitch());

    return () => {
      unsubscribe();
      stopWatch();
    };
  }, []);

  return null;
}
